const SITE_URL = "https://www.kccparts.ca";

const cleanText = (value = "") =>
  String(value || "")
    .replace(/\s+/g, " ")
    .trim();

const titleCase = (value = "") =>
  cleanText(value)
    .toLowerCase()
    .replace(/\b[a-z]/g, (letter) => letter.toUpperCase());

const isMeaningful = (value) => cleanText(value).length > 0;

const absoluteUrl = (value = "") => {
  const url = cleanText(value);
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${SITE_URL}${url.startsWith("/") ? url : `/${url}`}`;
};

const getProductType = (product = {}) => {
  const haystack = cleanText(
    [product.category, product.type, product.name, product.title, product.sku]
      .filter(Boolean)
      .join(" ")
  ).toLowerCase();

  let baseType = cleanText(product.category || product.type || "");

  if (/brake\s*(disc|rotor)|\brotors?\b|\bdiscs?\b|kcc[-\s]?(bd|br)\b/.test(haystack)) {
    baseType = "Brake Rotor";
  } else if (/brake\s*pads?|kcc[-\s]?bp\b|\bbp[-\s]?\d/.test(haystack)) {
    baseType = "Brake Pads";
  } else if (/oil\s*filters?/.test(haystack)) {
    baseType = "Oil Filter";
  } else if (/cabin\s*air\s*filters?/.test(haystack)) {
    baseType = "Cabin Air Filter";
  } else if (/air\s*filters?/.test(haystack)) {
    baseType = "Air Filter";
  } else if (baseType) {
    baseType = titleCase(baseType.replace(/\s*\/\s*/g, "/"));
  }

  const position = cleanText(product.position);
  const usablePosition = position && position.toLowerCase() !== "none";
  if (usablePosition && /brake (rotor|pads?)/i.test(baseType)) {
    return `${titleCase(position.replace(/-/g, " "))} ${baseType}`;
  }

  return baseType || cleanText(product.displayName || product.title || product.name || "Auto Part");
};

const yearRangeText = (years = []) => {
  const nums = [...new Set(years.map((year) => Number(year)).filter(Number.isFinite))].sort(
    (a, b) => a - b
  );
  if (!nums.length) return "";
  const min = nums[0];
  const max = nums[nums.length - 1];
  return min === max ? String(min) : `${min}-${max}`;
};

const getFitmentGroups = (fitments = []) => {
  const groups = new Map();

  (Array.isArray(fitments) ? fitments : []).forEach((fitment) => {
    const make = titleCase(fitment?.make);
    const model = titleCase(fitment?.model);
    if (!make || !model) return;

    const key = `${make}|${model}`;
    const existing = groups.get(key) || { make, model, years: [], count: 0 };
    if (fitment?.year != null) existing.years.push(fitment.year);
    existing.count += 1;
    groups.set(key, existing);
  });

  return [...groups.values()].sort((a, b) => b.count - a.count || a.make.localeCompare(b.make));
};

export const getPrimaryFitmentText = (fitments = [], { includeYears = true } = {}) => {
  const groups = getFitmentGroups(fitments);
  if (!groups.length) return "";

  if (groups.length === 1) {
    const group = groups[0];
    const years = includeYears ? yearRangeText(group.years) : "";
    return cleanText(`${group.make} ${group.model} ${years}`);
  }

  const [first, second] = groups;
  const sameMakeGroups = groups.filter((group) => group.make === first.make);

  if (sameMakeGroups.length >= 2) {
    const modelList = sameMakeGroups.slice(0, 2).map((group) => group.model).join(", ");
    return `${first.make} ${modelList}${groups.length > 2 ? " and more" : ""}`;
  }

  return `${first.make} ${first.model}, ${second.make} ${second.model}${groups.length > 2 ? " and more" : ""}`;
};

const getDescriptionFitmentText = (fitments = []) => {
  const groups = getFitmentGroups(fitments);
  if (!groups.length) return "";
  if (groups.length === 1) return `${groups[0].make} ${groups[0].model}`;
  const first = groups[0];
  return `${first.make} ${first.model} and other compatible vehicles`;
};

export const buildProductSeo = (product = {}, productId = "") => {
  const displayName = cleanText(product.displayName || product.title || product.name);
  const productType = getProductType(product);
  const fitmentText = getPrimaryFitmentText(product.fitments);

  const reference = cleanText(product.sku || product.partNumber);
  const baseTitle = fitmentText ? `${productType} for ${fitmentText}` : displayName || productType;
  const identity = reference && !baseTitle.includes(reference) ? ` ${reference}` : '';
  const title = `${baseTitle.slice(0, Math.max(35, 70 - identity.length))}${identity} | KCC Parts`;

  const descriptionFitment = getDescriptionFitmentText(product.fitments);
  const baseDescription = descriptionFitment
    ? `Shop ${productType.toLowerCase()} for ${descriptionFitment} at KCC Parts. Check fitment, SKU, availability, and order auto parts in Canada.`
    : `Shop ${displayName || productType} at KCC Parts. Check SKU, availability, and order auto parts in Canada.`;
  const description = `${baseDescription}${reference ? ` Reference: ${reference}.` : ''}${product.attributes?.Volume ? ` Size: ${product.attributes.Volume}.` : ''}`;

  const canonicalPath = `/product/${encodeURIComponent(String(productId || product._id || ""))}`;
  const image = Array.isArray(product.images) && product.images.length ? product.images[0] : product.image;
  const schemaDescriptionParts = [product.description, fitmentText ? `Fits ${fitmentText}.` : ""]
    .map(cleanText)
    .filter(Boolean);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: displayName || productType,
    description: schemaDescriptionParts.join(" ") || description,
    url: `${SITE_URL}${canonicalPath}`,
  };

  if (isMeaningful(product.sku)) jsonLd.sku = cleanText(product.sku);
  // Engine Oil imports use partNumber for the grade/variant, not an MPN.
  if (isMeaningful(product.partNumber) && !/^engine[- ]oil$/i.test(product.category || '')) jsonLd.mpn = cleanText(product.partNumber);
  if (isMeaningful(product.brand)) jsonLd.brand = { "@type": "Brand", name: cleanText(product.brand) };
  if (isMeaningful(image)) jsonLd.image = [absoluteUrl(image)];

  const now = new Date();
  const saleActive = product.salePrice != null && (!product.saleStartAt || new Date(product.saleStartAt) <= now) && (!product.saleEndAt || new Date(product.saleEndAt) >= now);
  const price = Number(saleActive ? product.salePrice : product.price);
  if (product.status !== 'comingSoon' && product.status !== 'draft' && product.price != null && Number.isFinite(price) && price >= 0) {
    jsonLd.offers = {
      "@type": "Offer",
      url: `${SITE_URL}${canonicalPath}`,
      price: price.toFixed(2),
      priceCurrency: "CAD",
      itemCondition: 'https://schema.org/NewCondition',
      seller: { '@type': 'Organization', name: 'KCC Parts Distribution', url: SITE_URL },
      availability:
          Number(product.stockQty || 0) > 0
            ? "https://schema.org/InStock"
            : "https://schema.org/OutOfStock",
    };
  }

  return { title, description, canonicalPath, jsonLd };
};
